<script setup lang="ts">
/**
 * Find / replace bar for the editor (#249), styled by `findBar.css`.
 * Render as a direct child of `.zw-editor-shell`.
 */
import { nextTick, onMounted, useTemplateRef } from "vue";
import { assertNever } from "../../lib/assertNever";
import { just, nothing } from "../../lib/maybe";
import { useTea } from "../../lib/tea";
import ZwInput from "../../components/ui/ZwInput.vue";
import type { ProseSearchCommand, ProseSearchQuery } from "./proseEditor";
import {
  initialProseFindBarModel,
  updateProseFindBarFn,
  type ProseFindBarCmd,
} from "./ProseFindBar.update";
import "./findBar.css";

const props = defineProps<{
  /** Selected text when opened; "" keeps the previous query. */
  seed: string;
}>();

const emit = defineEmits<{
  query: [query: ProseSearchQuery];
  run: [command: ProseSearchCommand];
  close: [];
}>();

const searchInput = useTemplateRef<InstanceType<typeof ZwInput>>("searchInput");
const replaceInput =
  useTemplateRef<InstanceType<typeof ZwInput>>("replaceInput");

async function runCmd(cmd: ProseFindBarCmd): Promise<void> {
  switch (cmd.kind) {
    case "query":
      emit("query", cmd.query);
      return;
    case "run":
      emit("run", cmd.command);
      return;
    case "focusSearch":
      await nextTick();
      searchInput.value?.focus();
      searchInput.value?.select();
      return;
    case "focusReplace":
      await nextTick();
      replaceInput.value?.focus();
      return;
    case "close":
      emit("close");
      return;
    default:
      assertNever(cmd);
  }
}

const { model, dispatch } = useTea(
  initialProseFindBarModel,
  updateProseFindBarFn,
  {
    runCmd: just(runCmd),
  },
);

/** Re-open with a fresh seed while already mounted (Mod-F again). */
function open(seed: string): void {
  void dispatch({ type: "Open", seed });
}

onMounted(() => open(props.seed));

function onSearchKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter") {
    event.preventDefault();
    void dispatch({ type: "Run", command: event.shiftKey ? "prev" : "next" });
  } else if (event.key === "Escape") {
    event.preventDefault();
    void dispatch({ type: "Close" });
  }
}

function onReplaceKeydown(event: KeyboardEvent): void {
  if (event.key === "Enter") {
    event.preventDefault();
    void dispatch({ type: "Run", command: "replace" });
  } else if (event.key === "Escape") {
    event.preventDefault();
    void dispatch({ type: "Close" });
  }
}

defineExpose({ open });
</script>

<template>
  <div class="zw-find-bar">
    <div class="zw-find" role="search">
      <div class="zw-find-row">
        <button
          type="button"
          name="expand"
          class="zw-find-toggle"
          aria-label="Toggle replace"
          :aria-expanded="model.expanded"
          @click="dispatch({ type: 'ExpandToggled' })"
        >
          {{ model.expanded ? "⌄" : "›" }}
        </button>
        <div class="zw-find-field">
          <ZwInput
            :id="nothing"
            ref="searchInput"
            :model-value="model.search"
            placeholder="Find"
            aria-label="Find"
            :label="nothing"
            @update:model-value="
              dispatch({ type: 'SearchChanged', value: $event })
            "
            @keydown="onSearchKeydown"
          />
        </div>
        <button
          type="button"
          class="zw-find-button"
          name="next"
          @click="dispatch({ type: 'Run', command: 'next' })"
        >
          next
        </button>
        <button
          type="button"
          class="zw-find-button"
          name="prev"
          @click="dispatch({ type: 'Run', command: 'prev' })"
        >
          previous
        </button>
        <label>
          <input
            type="checkbox"
            name="case"
            :checked="model.caseSensitive"
            @change="dispatch({ type: 'CaseToggled' })"
          />match case
        </label>
        <label>
          <input
            type="checkbox"
            name="word"
            :checked="model.wholeWord"
            @change="dispatch({ type: 'WordToggled' })"
          />by word
        </label>
        <button
          type="button"
          name="close"
          aria-label="Close search panel"
          @click="dispatch({ type: 'Close' })"
        >
          ×
        </button>
      </div>
      <div v-show="model.expanded" class="zw-find-row zw-find-row-indent">
        <div class="zw-find-field">
          <ZwInput
            :id="nothing"
            ref="replaceInput"
            :model-value="model.replace"
            placeholder="Replace"
            aria-label="Replace"
            :label="nothing"
            @update:model-value="
              dispatch({ type: 'ReplaceChanged', value: $event })
            "
            @keydown="onReplaceKeydown"
          />
        </div>
        <button
          type="button"
          class="zw-find-button"
          name="replace"
          @click="dispatch({ type: 'Run', command: 'replace' })"
        >
          replace
        </button>
        <button
          type="button"
          class="zw-find-button"
          name="replaceAll"
          @click="dispatch({ type: 'Run', command: 'replaceAll' })"
        >
          replace all
        </button>
      </div>
    </div>
  </div>
</template>
